import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Dialog, DialogContent, DialogTitle, Grid, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, IconButton } from '@mui/material';
import { Download, CloudUpload, Close } from '@mui/icons-material';
import CustomTextField from '../../CustomTextField';
import { headingBlue, red, lightGrey, mediumGrey } from '../../../assets/colors';
import { buttonStyle } from '../../../utils/commonCSS';
import HttpService from '../../../backend/requestApis';
import Loader from '../../Loader';
import { useDispatch } from 'react-redux';
import { showSnackBar } from '../../CustomComponents/CustomComponentsSlice';
import { uploadLearningMaterial, uploadCoordinatorLearningMaterial, fetchCoordinatorLearningMaterials } from '../../../backend/backendApis';
import moment from 'moment';

const LearningMaterialsDialog = (props) => {
    const { dialogStatus, updateFunc, classId, isCoordinator } = props;
    const dispatch = useDispatch();
    
    const [materials, setMaterials] = useState([]);
    const [isLoadingMaterials, setIsLoadingMaterials] = useState(false);
    
    const [isUploading, setIsUploading] = useState(false);
    const [file, setFile] = useState(null);
    const [title, setTitle] = useState('');
    const [uploadError, setUploadError] = useState('');
    
    const fileInputRef = useRef(null);

    const titleStyle = {
        backgroundColor: headingBlue, 
        color: 'white',
        fontSize: '1rem',
        display: "flex",
        alignItems: 'center',
        padding: "0.4rem 1rem 0.4rem 1rem",
        justifyContent: 'space-between'
    };

    const headerCellStyle = {
        padding: '0.4rem 0.5rem', 
        fontFamily: "Helvetica",
        fontSize: "0.9rem", 
        fontWeight: "600",
        textAlign: 'center',
        backgroundColor: lightGrey,
        borderBottom: `1px solid ${mediumGrey}`
    };

    const bodyCellStyle = {
        padding: '0.4rem 0.5rem', 
        fontFamily: "Helvetica",
        fontSize: "0.9rem", 
        fontWeight: "400",
        textAlign: 'center',
    };

    const fetchMaterials = () => {
        if (!classId) return;
        setIsLoadingMaterials(true);
        let options = {
            url: fetchCoordinatorLearningMaterials,
            method: 'GET',
            params: { classId: classId },
            headers: {
                Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null
            }
        };

        HttpService(options)
            .then(resp => {
                setMaterials(resp?.data?.data || resp?.data || []);
                setIsLoadingMaterials(false);
            })
            .catch(err => {
                console.error("Error fetching materials: ", err);
                dispatch(showSnackBar({ message: 'Error fetching materials. Please try again.', severity: 'error' }));
                setIsLoadingMaterials(false);
            });
    };

    useEffect(() => {
        if (dialogStatus && classId) {
            setFile(null);
            setTitle('');
            setUploadError('');
            fetchMaterials();
        }
    }, [dialogStatus, classId]);

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const selectedFile = e.target.files[0];
            // Basic validation check - limiting to 25MB as a safeguard
            if (selectedFile.size > 25 * 1024 * 1024) {
                setUploadError('File size exceeds 25MB limit.');
                setFile(null);
                return;
            }
            setFile(selectedFile);
            setUploadError('');
        }
    };

    const handleUpload = () => {
        if (!file) {
            setUploadError('Please select a file to upload.');
            return;
        }

        setIsUploading(true);
        setUploadError('');

        const formData = new FormData();
        formData.append('file', file);
        formData.append('classId', classId);
        if (title) {
            formData.append('title', title);
        }

        let options = {
            url: isCoordinator ? uploadCoordinatorLearningMaterial : uploadLearningMaterial,
            method: 'POST',
            payload: formData,
            headers: {
                headers: {
                    Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : '',
                    'Content-Type': 'multipart/form-data'
                }
            }
        };

        HttpService(options)
            .then(resp => {
                setIsUploading(false);
                dispatch(showSnackBar({ message: 'Material uploaded successfully!', severity: 'success' }));
                setFile(null);
                setTitle('');
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
                fetchMaterials();
            })
            .catch(err => {
                console.error("Error uploading material: ", err);
                setIsUploading(false);
                setUploadError(err?.response?.data?.message || 'Error uploading file. Please try again.');
            });
    };

    return (
        <Dialog maxWidth='md' fullWidth open={dialogStatus} onClose={() => updateFunc(false)}>
            <DialogTitle style={titleStyle}>
                <Typography style={{ fontWeight: 600 }}>Learning Materials</Typography>
                <IconButton onClick={() => updateFunc(false)} style={{ color: 'white', padding: 0 }}>
                    <Close />
                </IconButton>
            </DialogTitle>
             <DialogContent>
                {isLoadingMaterials && <Loader />}
                <Box style={{ padding: '1.5rem 0rem 0rem 0rem' }}>
                    {isCoordinator && (
                        <Box style={{ marginBottom: '2rem', padding: '1.5rem', border: `1px solid ${mediumGrey}`, borderRadius: '0.4rem', backgroundColor: '#f9f9f9' }}>
                            <Typography variant="subtitle2" style={{ fontWeight: 'bold', color: headingBlue, marginBottom: '1rem' }}>Upload New Material</Typography>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={5}>
                                    <input type="file" onChange={handleFileChange} ref={fileInputRef} style={{ display: 'block', width: '100%', padding: '0.3rem' }} />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <CustomTextField placeHolder='Custom Title (Optional)' value={title} setFunc={setTitle} />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <Button style={{ ...buttonStyle, width: '100%', padding: '0.4rem' }} onClick={handleUpload} disabled={isUploading || !file} startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : <CloudUpload />}>
                                        {isUploading ? 'Uploading...' : 'Upload'}
                                    </Button>
                                </Grid>
                            </Grid>
                            {uploadError && <Typography style={{ color: red, fontSize: '0.8rem', marginTop: '0.5rem' }}>{uploadError}</Typography>}
                            {file && !uploadError && <Typography style={{ color: headingBlue, fontSize: '0.8rem', marginTop: '0.5rem' }}>Selected: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)</Typography>}
                        </Box>
                    )}

                    {/* List Section */}
                    <Typography variant="subtitle2" style={{ fontWeight: 'bold', color: headingBlue, marginBottom: '1rem' }}>Existing Materials</Typography>
                    <TableContainer component={Paper} style={{ border: `1px solid ${mediumGrey}`, boxShadow: 'none', maxHeight: '40vh', overflowY: 'auto' }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell style={headerCellStyle}>Title / File Name</TableCell>
                                    <TableCell style={headerCellStyle}>Uploaded Date</TableCell>
                                    <TableCell style={headerCellStyle}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(isLoadingMaterials || materials.length === 0) ? (
                                    <TableRow>
                                        <TableCell colSpan={3} style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                                            {!isLoadingMaterials && "No materials found for this class."}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    materials.map((mat, index) => (
                                        <TableRow key={index}>
                                            <TableCell style={bodyCellStyle}>{mat.title || 'Untitled Document'}</TableCell>
                                            <TableCell style={bodyCellStyle}>{mat.uploadedAt ? moment(mat.uploadedAt).utcOffset('+05:30').format('DD/MM/YYYY') : '-'}</TableCell>
                                            <TableCell style={bodyCellStyle}>
                                                <Button variant="outlined" size="small" style={{ color: headingBlue, borderColor: headingBlue, textTransform: 'capitalize' }} startIcon={<Download fontSize="small" />} onClick={() => window.open(mat.downloadUrl, '_blank')}>
                                                    Download
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box style={{ padding: '1rem 0rem 0rem 0rem', display: 'flex', justifyContent: 'flex-end' }}>
                        <Button style={{ ...buttonStyle, backgroundColor: red, padding: '0.3rem 1.5rem' }} onClick={() => updateFunc(false)}>
                            Close
                        </Button>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default LearningMaterialsDialog;